/**
 * Renderer properties for the map-layer review previews.
 *
 * The location ("source") preview draws the imported GeoJSON with the same
 * appearance the reviewer is choosing for publication, so the preview answers
 * "what will this look like on the project map?" rather than showing a fixed
 * review colour. The publisher builds its symbols in
 * `ProjectMapPublishingService.selections()`; the shapes below mirror those
 * rules (point → colour + size, line → colour + width, polygon → filled with
 * `fillAlpha` and outlined in the same colour, and a colour-by field becomes a
 * unique-value renderer whose default symbol is the plain layer colour).
 *
 * This module deliberately imports nothing: it holds no @arcgis/core import so
 * it can be unit tested in karma, and it returns plain property objects that
 * the SDK autocasts when assigned to `layer.renderer`.
 */

/** GeoJSONLayer geometry types the source preview splits features into. */
export type MapSourcePreviewFamily = 'point' | 'multipoint' | 'polyline' | 'polygon';

export interface MapSourcePreviewColorBy {
  field: string;
  /**
   * `fillColor` fills a polygon differently from its outline, matching what the
   * publisher writes when the imported file authored a separate fill.
   * `label` is the legend name; the preview never draws it.
   */
  values: { value: string | number; color: string; label?: string; fillColor?: string }[];
}

/** The subset of the review settings form that changes how features draw. */
export interface MapSourcePreviewSettings {
  color: string;
  strokeWidth: number;
  pointSize: number;
  fillAlpha: number;
  colorBy?: MapSourcePreviewColorBy | null;
}

export type MapSourcePreviewSymbol =
  | { type: 'simple-marker'; color: string; size: number }
  | { type: 'simple-line'; color: string; width: number }
  | { type: 'simple-fill'; color: [number, number, number, number]; outline: { color: string; width: number } };

export type MapSourcePreviewRenderer =
  | { type: 'simple'; symbol: MapSourcePreviewSymbol }
  | {
      type: 'unique-value';
      field: string;
      defaultSymbol: MapSourcePreviewSymbol;
      uniqueValueInfos: { value: string | number; symbol: MapSourcePreviewSymbol }[];
    };

/** The colour the review form starts from, and the fallback for an unusable one. */
export const MAP_SOURCE_PREVIEW_DEFAULT_COLOR = '#1036d6';
const DEFAULT_STROKE_WIDTH = 2;
const DEFAULT_POINT_SIZE = 8;
const DEFAULT_FILL_ALPHA = 0.35;
// The form's own limits, so a half-typed number never produces an invisible map.
const MAX_STROKE_WIDTH = 64;
const MAX_POINT_SIZE = 128;
const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;

function normalizedColor(value: unknown, fallback = MAP_SOURCE_PREVIEW_DEFAULT_COLOR): string {
  const color = typeof value === 'string' ? value.trim() : '';
  return HEX_COLOR.test(color) ? color.toLowerCase() : fallback;
}

function normalizedNumber(value: unknown, min: number, max: number, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? Math.min(max, Math.max(min, value)) : fallback;
}

/** `#rrggbb` plus an alpha channel, the array form the SDK reads for a fill. */
function rgba(color: string, alpha: number): [number, number, number, number] {
  return [
    parseInt(color.slice(1, 3), 16),
    parseInt(color.slice(3, 5), 16),
    parseInt(color.slice(5, 7), 16),
    alpha,
  ];
}

function symbolFor(family: MapSourcePreviewFamily, color: string, settings: MapSourcePreviewSettings,
                   fillColor?: string): MapSourcePreviewSymbol {
  const strokeWidth = normalizedNumber(settings.strokeWidth, 0, MAX_STROKE_WIDTH, DEFAULT_STROKE_WIDTH);
  if (family === 'point' || family === 'multipoint') {
    return { type: 'simple-marker', color, size: normalizedNumber(settings.pointSize, 0, MAX_POINT_SIZE, DEFAULT_POINT_SIZE) };
  }
  if (family === 'polyline') return { type: 'simple-line', color, width: strokeWidth };
  return {
    type: 'simple-fill',
    color: rgba(normalizedColor(fillColor, color), normalizedNumber(settings.fillAlpha, 0, 1, DEFAULT_FILL_ALPHA)),
    outline: { color, width: strokeWidth },
  };
}

/**
 * Renderer properties for one geometry family of the source preview.
 *
 * `colorByField` names the field to match on. It defaults to the field chosen
 * in the settings; pass `null` when the previewed layer has no such field so
 * the layer falls back to the plain layer colour instead of drawing nothing.
 */
export function mapSourcePreviewRenderer(
  settings: MapSourcePreviewSettings,
  family: MapSourcePreviewFamily,
  colorByField: string | null = settings?.colorBy?.field ?? null
): MapSourcePreviewRenderer {
  const safe: MapSourcePreviewSettings = settings || ({} as MapSourcePreviewSettings);
  const color = normalizedColor(safe.color);
  const defaultSymbol = symbolFor(family, color, safe);
  const field = typeof colorByField === 'string' ? colorByField.trim() : '';
  const entries = field ? safe.colorBy?.values || [] : [];
  const seen = new Set<string>();
  const uniqueValueInfos: { value: string | number; symbol: MapSourcePreviewSymbol }[] = [];
  for (const entry of entries) {
    const numeric = typeof entry?.value === 'number' && Number.isFinite(entry.value);
    if (!numeric && (typeof entry?.value !== 'string' || !entry.value.length)) continue;
    // The renderer matches on the first entry for a value, so a repeat would
    // never draw; keep the colour the reviewer sees first in the list.
    const key = `${typeof entry.value}:${entry.value}`;
    if (seen.has(key)) continue;
    seen.add(key);
    uniqueValueInfos.push({ value: entry.value,
      symbol: symbolFor(family, normalizedColor(entry.color, color), safe, entry.fillColor) });
  }
  if (!uniqueValueInfos.length) return { type: 'simple', symbol: defaultSymbol };
  return { type: 'unique-value', field, defaultSymbol, uniqueValueInfos };
}
